/**
 * Oh Crap — Stockist Locator
 *
 * Reads a published Google Sheet CSV, groups physical stores by state,
 * lists online retailers, and supports filtering by state.
 *
 * Expected CSV columns (case-insensitive, all optional except `name`):
 *   name      retailer / store name (required)
 *   type      "physical" or "online" — defaults to physical
 *   state     used to group physical stores
 *   address   physical address (\n in the cell becomes <br>)
 *   phone     phone number
 *   url       link target — also accepts "website" or "link"
 */
(function () {
  if (window.__ohCrapStockistLocator) return;
  window.__ohCrapStockistLocator = true;

  class StockistLocator {
    constructor(el) {
      this.el                 = el;
      this.csvUrl             = el.dataset.csvUrl || '';
      this.physicalHeading    = el.dataset.physicalHeading || 'Physical Stores';
      this.onlineHeading      = el.dataset.onlineHeading   || 'Shop Online';
      this.footerMessage      = el.dataset.footerMessage   || '';
      this.filterLabel        = el.dataset.filterLabel     || 'Filter by state';
      this.allLabel           = el.dataset.allLabel        || 'All';
      this.errorText          = el.dataset.errorText       || "Couldn't load stockist data.";
      this.emptyText          = el.dataset.emptyText       || 'No stockists to display yet.';
      this.sectionTitleClass  = el.dataset.sectionTitleClass || 'stockist-locator__section-title';
      this.stateHeadingClass  = el.dataset.stateHeadingClass || 'stockist-locator__state-heading';
      this.activeState        = 'all';
      this.physicalStores     = [];
      this.onlineRetailers    = [];
      this.states             = [];
      this.init();
    }

    async init() {
      if (!this.csvUrl) {
        this.renderError('No CSV URL configured for the Stockist Locator section.');
        return;
      }
      const url = normalizeSheetUrl(this.csvUrl);
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const text = await res.text();
        this.parseCsv(text);
        this.render();
      } catch (err) {
        console.error('[stockist-locator]', err);
        this.renderError(this.errorText);
      }
    }

    parseCsv(text) {
      const rows = parseRows(text);
      if (rows.length === 0) return;

      const header = rows[0].map((c) => c.trim().toLowerCase());
      const idx = (...names) => {
        for (const n of names) {
          const i = header.indexOf(n);
          if (i !== -1) return i;
        }
        return -1;
      };
      const cell = (r, i) => (i !== -1 ? (r[i] || '').trim() : '');
      const truthy = (v) => /^(y|yes|true|1|x|✓)$/i.test(String(v || '').trim());

      const iName     = idx('name', 'retailer name', 'retailer', 'store');
      const iAddr1    = idx('address line 1', 'address1', 'street', 'address');
      const iAddr2    = idx('address line 2', 'address2');
      const iSuburb   = idx('suburb', 'city', 'town');
      const iState    = idx('state', 'region');
      const iPostcode = idx('postcode', 'post code', 'zip', 'zipcode');
      const iCountry  = idx('country');
      const iPhone    = idx('phone', 'tel', 'telephone');
      const iUrl      = idx('website', 'url', 'link');
      const iType     = idx('type', 'category');
      const iPhysical = idx('has physical location', 'physical', 'is physical', 'in store');
      const iOnline   = idx('has online store', 'online', 'is online', 'online store');

      const physical = [];
      const online   = [];
      const stateSet = new Set();

      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r || r.every((c) => !c.trim())) continue;
        const name = cell(r, iName);
        if (!name) continue;

        // Build a multi-line address from the parts we have.
        const line1 = cell(r, iAddr1);
        const line2 = cell(r, iAddr2);
        const suburb = cell(r, iSuburb);
        const state = cell(r, iState);
        const postcode = cell(r, iPostcode);
        const country = cell(r, iCountry);
        const cityLine = [suburb, state, postcode].filter(Boolean).join(' ');
        const address = [line1, line2, cityLine, country].filter(Boolean).join('\n');

        // Normalize URL — bare domains like "petdeli.au" need a scheme.
        let url = cell(r, iUrl);
        if (url && !/^https?:\/\//i.test(url) && !/^mailto:|^tel:/i.test(url)) {
          url = 'https://' + url.replace(/^\/+/, '');
        }

        const item = {
          name,
          address,
          phone: cell(r, iPhone),
          state,
          url,
        };

        // Decide placement.
        // - Has Physical/Online flag columns take priority when present.
        // - Otherwise fall back to a single `type` column.
        // - Otherwise default to physical (the dataset is overwhelmingly physical).
        let isPhysical = false;
        let isOnline = false;
        if (iPhysical !== -1 || iOnline !== -1) {
          isPhysical = iPhysical !== -1 && truthy(r[iPhysical]);
          isOnline   = iOnline   !== -1 && truthy(r[iOnline]);
          if (!isPhysical && !isOnline) isPhysical = true;
        } else if (iType !== -1) {
          const t = (r[iType] || '').trim().toLowerCase();
          isOnline = t === 'online';
          isPhysical = !isOnline;
        } else {
          isPhysical = true;
        }

        if (isPhysical) {
          physical.push(item);
          if (state) stateSet.add(state);
        }
        if (isOnline) online.push(item);
      }

      physical.sort((a, b) =>
        (a.state || '').localeCompare(b.state || '') || a.name.localeCompare(b.name)
      );
      online.sort((a, b) => a.name.localeCompare(b.name));

      this.physicalStores  = physical;
      this.onlineRetailers = online;
      this.states          = Array.from(stateSet).sort();
    }

    render() {
      const out        = [];
      const hasPhysical = this.physicalStores.length > 0;
      const hasOnline   = this.onlineRetailers.length > 0;

      // Filters — only when there are 2+ states to choose between
      if (this.states.length > 1) {
        out.push('<div class="stockist-locator__filters">');
        out.push('<span class="stockist-locator__filter-label">' + esc(this.filterLabel) + '</span>');
        out.push('<div class="stockist-locator__filter-buttons">');
        out.push('<button type="button" class="stockist-locator__filter-btn active" data-state="all">' + esc(this.allLabel) + '</button>');
        for (const s of this.states) {
          out.push('<button type="button" class="stockist-locator__filter-btn" data-state="' + esc(s) + '">' + esc(s) + '</button>');
        }
        out.push('</div></div>');
      }

      // Physical stores
      if (hasPhysical) {
        out.push('<div class="stockist-locator__section stockist-locator__section--physical">');
        out.push('<h2 class="' + esc(this.sectionTitleClass) + '">' + esc(this.physicalHeading) + '</h2>');
        for (const s of this.states) {
          const stores = this.physicalStores.filter((p) => p.state === s);
          if (!stores.length) continue;
          out.push('<div class="stockist-locator__state-group" data-state="' + esc(s) + '">');
          out.push('<h3 class="' + esc(this.stateHeadingClass) + '">' + esc(s) + '</h3>');
          out.push('<ul class="stockist-locator__retailer-list">');
          for (const store of stores) out.push(this.renderStore(store));
          out.push('</ul></div>');
        }
        const noState = this.physicalStores.filter((p) => !p.state);
        if (noState.length) {
          out.push('<div class="stockist-locator__state-group" data-state="">');
          out.push('<ul class="stockist-locator__retailer-list">');
          for (const store of noState) out.push(this.renderStore(store));
          out.push('</ul></div>');
        }
        out.push('</div>');
      }

      // Footer message between sections
      if (this.footerMessage && (hasPhysical || hasOnline)) {
        out.push('<div class="stockist-locator__footer-message">' + esc(this.footerMessage) + '</div>');
      }

      // Online retailers
      if (hasOnline) {
        out.push('<div class="stockist-locator__section stockist-locator__section--online">');
        out.push('<h2 class="' + esc(this.sectionTitleClass) + '">' + esc(this.onlineHeading) + '</h2>');
        out.push('<ul class="stockist-locator__online-list">');
        for (const r of this.onlineRetailers) {
          out.push('<li class="stockist-locator__online-retailer">' + formatLink(r.name, r.url) + '</li>');
        }
        out.push('</ul></div>');
      }

      if (!hasPhysical && !hasOnline) {
        out.push('<div class="stockist-locator__empty">' + esc(this.emptyText) + '</div>');
      }

      this.el.innerHTML = out.join('');
      this.bindFilters();
    }

    renderStore(store) {
      const parts = ['<li class="stockist-locator__retailer">'];
      parts.push('<div class="stockist-locator__retailer-name">' + formatLink(store.name, store.url) + '</div>');
      if (store.address) {
        parts.push('<div class="stockist-locator__retailer-address">' + esc(store.address).replace(/\n/g, '<br>') + '</div>');
      }
      if (store.phone) {
        const tel = store.phone.replace(/[^\d+]/g, '');
        parts.push('<div class="stockist-locator__retailer-phone"><a href="tel:' + esc(tel) + '">' + esc(store.phone) + '</a></div>');
      }
      parts.push('</li>');
      return parts.join('');
    }

    bindFilters() {
      const btns = this.el.querySelectorAll('.stockist-locator__filter-btn');
      btns.forEach((btn) => {
        btn.addEventListener('click', () => {
          btns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          this.activeState = btn.dataset.state;
          this.applyFilter();
        });
      });
    }

    applyFilter() {
      const groups = this.el.querySelectorAll('.stockist-locator__state-group');
      groups.forEach((g) => {
        const show = this.activeState === 'all' || g.dataset.state === this.activeState;
        g.classList.toggle('hidden', !show);
      });
    }

    renderError(msg) {
      this.el.innerHTML = '<div class="stockist-locator__error">' + esc(msg) + '</div>';
    }
  }

  // ── Helpers ────────────────────────────────────────────────

  // Accepts any "Publish to web" Google Sheet URL and coerces it to CSV.
  // Handles: pubhtml, pub?output=csv, /edit, /edit#gid=…, plain /e/ URLs.
  function normalizeSheetUrl(input) {
    if (!input) return input;
    let url;
    try { url = new URL(input); } catch { return input; }
    if (!/docs\.google\.com$/.test(url.hostname)) return input;

    // Pull a gid out of either the search params or the hash (#gid=123)
    const hashMatch = url.hash && url.hash.match(/gid=(\d+)/);
    const gid = url.searchParams.get('gid') || (hashMatch && hashMatch[1]);

    // /spreadsheets/d/e/<token>/pubhtml  →  /spreadsheets/d/e/<token>/pub?output=csv
    if (/\/pubhtml\b/.test(url.pathname)) {
      url.pathname = url.pathname.replace(/\/pubhtml\b.*$/, '/pub');
      url.search = '';
      url.hash = '';
      url.searchParams.set('output', 'csv');
      if (gid) url.searchParams.set('gid', gid);
      return url.toString();
    }

    // /spreadsheets/d/e/<token>/pub  → ensure output=csv
    if (/\/pub\b/.test(url.pathname)) {
      if (!url.searchParams.get('output')) url.searchParams.set('output', 'csv');
      if (gid && !url.searchParams.get('gid')) url.searchParams.set('gid', gid);
      url.hash = '';
      return url.toString();
    }

    // /spreadsheets/d/<id>/edit…  → /spreadsheets/d/<id>/export?format=csv[&gid=…]
    const editMatch = url.pathname.match(/\/spreadsheets\/d\/([^/]+)\//);
    if (editMatch && /\/edit/.test(url.pathname)) {
      url.pathname = '/spreadsheets/d/' + editMatch[1] + '/export';
      url.search = '';
      url.hash = '';
      url.searchParams.set('format', 'csv');
      if (gid) url.searchParams.set('gid', gid);
      return url.toString();
    }

    return input;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (m) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[m]);
  }

  function formatLink(name, url) {
    if (!url) return esc(name);
    return '<a href="' + esc(url) + '" target="_blank" rel="noopener noreferrer">' + esc(name) + '</a>';
  }

  // RFC-4180-ish CSV parser: handles quoted fields, escaped quotes, and CRLF/LF.
  function parseRows(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { cell += '"'; i++; }
          else inQuotes = false;
        } else {
          cell += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cell);
        cell = '';
      } else if (ch === '\n' || ch === '\r') {
        if (cell.length || row.length) {
          row.push(cell);
          rows.push(row);
        }
        row = [];
        cell = '';
        if (ch === '\r' && text[i + 1] === '\n') i++;
      } else {
        cell += ch;
      }
    }
    if (cell.length || row.length) {
      row.push(cell);
      rows.push(row);
    }
    return rows;
  }

  // ── Bootstrap ──────────────────────────────────────────────
  function init(root) {
    (root || document).querySelectorAll('[data-stockist-locator]').forEach((el) => {
      if (el.__stockistInit) return;
      el.__stockistInit = true;
      new StockistLocator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-init on theme editor section reload
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-stockist-locator]').forEach((el) => {
      el.__stockistInit = false;
    });
    init(e.target);
  });
})();
